import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Users, MapPin, Heart, BookOpen, Shield } from 'lucide-react';
export function CopticPeoplePage() {
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-539">
        <div className="text-center mb-12" data-id="element-540">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-541">
            The Coptic People
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-542">
            Understanding the history, culture, and challenges of Egypt's
            largest Christian minority.
          </p>
        </div>
      </Section>
      {/* Who Are the Copts? */}
      <Section background="white" data-id="element-543">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-544">
          <div data-id="element-545">
            <div className="flex items-center mb-4" data-id="element-546">
              <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-547">
                <Users size={24} className="text-white" data-id="element-548" />
              </div>
              <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue" data-id="element-549">
                Who Are the Coptic People?
              </h2>
            </div>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-550">
              The Coptic Orthodox Christians are the indigenous Christians of
              Egypt, with a rich history dating back to the 1st century AD when
              Saint Mark the Evangelist brought Christianity to Egypt. The word
              "Copt" is derived from the Greek word "Aigyptos," meaning
              Egyptian.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-551">
              Today, Copts constitute the largest Christian community in the
              Middle East, making up approximately 10-15% of Egypt's
              population—roughly 10-12 million people. They maintain a distinct
              cultural identity, with their own language (Coptic, though now
              primarily used in liturgy), art, music, and religious traditions.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-552">
              The Coptic Orthodox Church is one of the oldest Christian
              institutions in the world, having preserved its faith through
              centuries of challenges. Coptic communities are known for their
              strong family bonds, deep spiritual devotion, and resilience in
              the face of adversity.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-553">
            <img src="https://images.unsplash.com/photo-1548019979-e7ce0fd1e813?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Coptic Orthodox Church in Egypt" className="w-full h-full object-cover" data-id="element-554" />
          </div>
        </div>
      </Section>
      {/* Challenges Faced by Coptic Communities */}
      <Section background="sand" data-id="element-555">
        <div className="mb-12 text-center" data-id="element-556">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-557">
            Challenges Faced by Coptic Communities
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-558">
            Despite their rich heritage and contributions to Egyptian society,
            Coptic communities face significant challenges.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-559">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-560">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-561">
              Systemic Challenges
            </h3>
            <ul className="space-y-4" data-id="element-562">
              <li className="flex items-start" data-id="element-563">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-564">
                  <MapPin size={16} className="text-white" data-id="element-565" />
                </div>
                <div data-id="element-566">
                  <h4 className="font-playfair font-bold text-primary-deep-blue" data-id="element-567">
                    Geographic Isolation
                  </h4>
                  <p className="font-inter text-neutral-charcoal" data-id="element-568">
                    Many Coptic communities are concentrated in rural Upper
                    Egypt and isolated urban enclaves, often with limited access
                    to essential services including healthcare.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-569">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-570">
                  <Shield size={16} className="text-white" data-id="element-571" />
                </div>
                <div data-id="element-572">
                  <h4 className="font-playfair font-bold text-primary-deep-blue" data-id="element-573">
                    Discrimination
                  </h4>
                  <p className="font-inter text-neutral-charcoal" data-id="element-574">
                    Copts face various forms of discrimination, including
                    limited representation in government, restrictions on church
                    building, and obstacles in education and employment
                    opportunities.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-575">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-576">
                  <Heart size={16} className="text-white" data-id="element-577" />
                </div>
                <div data-id="element-578">
                  <h4 className="font-playfair font-bold text-primary-deep-blue" data-id="element-579">
                    Healthcare Disparities
                  </h4>
                  <p className="font-inter text-neutral-charcoal" data-id="element-580">
                    Healthcare access is often limited in predominantly Coptic
                    areas, with fewer facilities, understaffed clinics, and
                    shortages of medical supplies and medications.
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-581">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-582">
              Healthcare Challenges
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-583">
              Healthcare disparities particularly affect Coptic communities in
              several ways:
            </p>
            <ul className="space-y-2" data-id="element-584">
              <li className="flex items-start" data-id="element-585">
                <div className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-586">
                  •
                </div>
                <span className="font-inter text-neutral-charcoal" data-id="element-587">
                  <strong data-id="element-588">Limited Resources:</strong> Rural clinics serving
                  Coptic communities often lack basic supplies, medications, and
                  equipment.
                </span>
              </li>
              <li className="flex items-start" data-id="element-589">
                <div className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-590">
                  •
                </div>
                <span className="font-inter text-neutral-charcoal" data-id="element-591">
                  <strong data-id="element-592">Accessibility Issues:</strong> Many must travel long
                  distances to receive adequate care, creating barriers for
                  elderly patients, pregnant women, and those with chronic
                  conditions.
                </span>
              </li>
              <li className="flex items-start" data-id="element-593">
                <div className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-594">
                  •
                </div>
                <span className="font-inter text-neutral-charcoal" data-id="element-595">
                  <strong data-id="element-596">Cultural Sensitivity:</strong> Some Copts report
                  experiencing discrimination when seeking care at public
                  facilities.
                </span>
              </li>
              <li className="flex items-start" data-id="element-597">
                <div className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-598">
                  •
                </div>
                <span className="font-inter text-neutral-charcoal" data-id="element-599">
                  <strong data-id="element-600">Preventative Care:</strong> Limited access to
                  preventative services leads to higher rates of preventable
                  diseases and complications.
                </span>
              </li>
            </ul>
            <div className="bg-neutral-offwhite p-4 rounded-lg mt-4" data-id="element-601">
              <p className="font-inter text-neutral-charcoal" data-id="element-602">
                <strong data-id="element-603">Monastery Clinics:</strong> In response to these
                challenges, many Coptic monasteries have established clinics to
                serve their communities. These clinics provide culturally
                sensitive care but often struggle with limited resources and
                supplies.
              </p>
            </div>
          </div>
        </div>
      </Section>
      {/* Why Targeted Support Matters */}
      <Section background="white" data-id="element-604">
        <div className="text-center mb-12" data-id="element-605">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-606">
            Why Targeted Support Matters
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-607">
            Directing medical aid to monastery clinics serving Coptic
            communities creates meaningful impact in several key ways.
          </p>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-608">
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md mb-8" data-id="element-609">
            <div className="flex items-center mb-4" data-id="element-610">
              <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-611">
                <BookOpen size={24} className="text-white" data-id="element-612" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-613">
                Cultural Understanding
              </h3>
            </div>
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-614">
              Monastery clinics understand the unique cultural context and needs
              of the communities they serve. They provide care in a familiar,
              trusted environment where patients feel comfortable and respected.
              By supporting these clinics, we ensure resources reach those who
              might otherwise avoid seeking care due to cultural barriers or
              discrimination concerns.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-615">
            <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-616">
              <h4 className="font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-617">
                Trusted Access Points
              </h4>
              <p className="font-inter text-neutral-charcoal" data-id="element-618">
                Monasteries are central institutions in Coptic communities,
                serving not just spiritual but social needs. Their clinics are
                trusted access points for healthcare, especially in areas where
                public services are limited or unwelcoming.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-619">
              <h4 className="font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-620">
                Service to All
              </h4>
              <p className="font-inter text-neutral-charcoal" data-id="element-621">
                While rooted in Coptic communities, monastery clinics serve
                people of all faiths and backgrounds. They embody the Christian
                values of compassion and service to all who are in need,
                regardless of religious affiliation.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-622">
              <h4 className="font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-623">
                Sustainable Impact
              </h4>
              <p className="font-inter text-neutral-charcoal" data-id="element-624">
                By working through established monastery clinics, we ensure
                donations create sustainable impact. These institutions have
                deep roots in their communities and will continue serving
                long-term.
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-primary-royal-blue/20" data-id="element-625">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-626">
              Our Approach
            </h3>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-627">
              Farasha Medical Aid targets support to monastery clinics not as an
              exclusionary practice, but as a strategic approach to ensure
              medical supplies reach communities that often fall through the
              cracks of traditional aid programs. By understanding the specific
              cultural context and healthcare landscape, we can maximize the
              impact of every donation.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-628">
              Our work honors both environmental responsibility by reducing
              medical waste and religious-cultural solidarity by supporting
              institutions that have long served as pillars of healthcare in
              their communities.
            </p>
          </div>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-629">
        <div className="text-center" data-id="element-630">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-631">
            Help Us Bridge Healthcare Gaps
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-632">
            Your support enables us to provide essential medical supplies to
            monastery clinics serving underserved Coptic communities in Egypt.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-633">
            <Button href="/donate" size="lg" data-id="element-634">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-635">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}